import { useEffect, useRef, useState } from 'react'
import { createChart, CrosshairMode, LineStyle, TickMarkType } from 'lightweight-charts'
import { getContrastColor } from '@/utils/contrast'
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/logger'
import { computePriceFormat } from '@/utils/pricePrecision'
import { decodeSavedRange, encodeSavedRange, lastBarLogical } from './visibleRangeStorage'

// Id de instancia de la serie principal: contador de MÓDULO, único también
// entre remounts por key={chartType}. Permite que la traza de identidad de
// setData (punto 3 del fix del blanco SOL/ETH) diga en una línea qué instancia
// recibió cada setData y cuál es la pintada.
let nextMainSeriesId = 1

// Autoscale estable en vivo (punto 5 del fix): la escala de precio NO se
// re-ajusta por tick. Mientras el usuario está en el borde vivo (el rango
// visible llega a la última vela), el provider SOLO EXPANDE el rango cuando el
// precio sale de lo visible y NUNCA encoge — el gráfico no se mueve solo
// jamás. En cuanto el usuario panea/zooma fuera del borde vivo, se delega en
// el autoscale natural de lightweight-charts (y el ancla se descarta, para que
// al volver al borde se reconstruya desde lo visible).
//
// `getResetKey` (símbolo+timeframe) es OBLIGATORIO para que el ancla no
// sobreviva a un cambio de dataset: sin él, el ancla expand-only de un símbolo
// (o la degenerada del vaciado de la transición) se quedaba clavada para
// todos los símbolos posteriores (regresión vista en staging: escala
// −4.000…72.000 con ETH/SOL aplastados). `isTransitioning` cierra la carrera
// residual: durante la transición de símbolo/TF (serie vaciada, histórico
// nuevo aún no aplicado) se devuelve el autoscale natural SIN anclar, para que
// un original() transitorio de la transición no envenene el ancla nueva.
export function createStableLiveAutoscale({ getVisibleLogicalRange, getDataLength, getResetKey, isTransitioning, getExtraRange, edgeMargin = 2 }) {
  let anchor = null
  let lastResetKey = null
  return (original) => {
    const res = original()
    if (!res || !res.priceRange) return res
    const resetKey = getResetKey?.() ?? null
    if (resetKey !== lastResetKey) {
      lastResetKey = resetKey
      anchor = null
    }
    let transitioning = false
    try { transitioning = !!isTransitioning?.() } catch { transitioning = false }
    if (transitioning) { anchor = null; return res }
    const len = getDataLength?.() ?? 0
    if (len <= 0) { anchor = null; return res }
    let atLiveEdge = false
    try {
      const r = getVisibleLogicalRange?.()
      atLiveEdge = !!r && r.to >= len - edgeMargin
    } catch { atLiveEdge = false }
    if (!atLiveEdge) { anchor = null; return res }
    // Rango extra opcional (cockpit, Fase 5.0): min/max de las priceLines
    // del trade (entrada/SL/TPs). lightweight-charts NO incluye las custom
    // price lines en el autoscale (Series._autoscaleInfoImpl solo mira
    // barras + márgenes de markers) — sin este merge, los niveles fuera del
    // rango de las velas (p.ej. TP2/TP3) arrancan fuera de cuadro. Misma
    // semántica expand-only: solo amplía, nunca encoge.
    let extra = null
    try { extra = getExtraRange?.() ?? null } catch { extra = null }
    const { minValue, maxValue } = res.priceRange
    if (!anchor) {
      anchor = {
        min: Math.min(minValue, extra?.min ?? Infinity),
        max: Math.max(maxValue, extra?.max ?? -Infinity),
      }
      if (extra) return { ...res, priceRange: { minValue: anchor.min, maxValue: anchor.max } }
      return res
    }
    const min = Math.min(anchor.min, minValue, extra?.min ?? Infinity)
    const max = Math.max(anchor.max, maxValue, extra?.max ?? -Infinity)
    anchor = { min, max }
    return { ...res, priceRange: { minValue: min, maxValue: max } }
  }
}

// Margen derecho útil («pared invisible», 2026-10-22): barras vacías a la
// derecha de la última vela, estilo TradingView, donde también se puede
// dibujar (ver drawingCoords.js). Es el rightOffset del timeScale y TAMBIÉN el
// hueco del rango visible inicial: setVisibleLogicalRange() fija el offset
// por su cuenta, y el arranque antiguo ({to: length}) lo dejaba en ~1 barra.
export const RIGHT_OFFSET_BARS = 20

// Rango visible inicial: las últimas `count` velas + RIGHT_OFFSET_BARS de aire.
export function initialVisibleRange(length, count) {
  return { from: length - count, to: length - 1 + RIGHT_OFFSET_BARS }
}

// Al cambiar de tipo de vela (ChartPage/ChartPublicPage montan este
// componente con `key={chartType}`, ver más abajo) el chart anterior se
// destruye y se crea uno nuevo. lightweight-charts gestiona el tamaño de sus
// canvases internos (fancy-canvas) con un ResizeObserver propio — al
// desconectarlo durante `chart.remove()` puede quedar un callback ya
// encolado por el navegador que se ejecuta igualmente justo después,
// intentando tocar un canvas ya liberado y lanzando "Object is disposed" de
// forma asíncrona (no hay ningún call site nuestro que envolver en
// try/catch: el throw ocurre dentro de la librería, en respuesta a un
// evento del propio navegador). No rompe nada visible — verificado
// manualmente cambiando de tipo varias veces seguidas — así que se silencia
// aquí en vez de dejarlo como excepción global sin capturar.
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    if (e.error?.message === 'Object is disposed') {
      e.preventDefault()
    }
  })
}

// lightweight-charts exige velas ordenadas y sin tiempos duplicados o el
// "colorer" del candlestick series lanza "Value is null" de forma diferida
// (https://github.com/tradingview/lightweight-charts/issues/568).
function sanitizeCandles(arr) {
  const sorted = [...arr]
    .filter(c => c && c.time != null && c.time !== '' && !Number.isNaN(c.time))
    .sort((a, b) => (a.time > b.time ? 1 : a.time < b.time ? -1 : 0))
  const out = []
  for (const c of sorted) {
    if (out.length > 0 && out[out.length - 1].time === c.time) {
      out[out.length - 1] = c
    } else {
      out.push(c)
    }
  }
  return out
}

function sameCandle(a, b) {
  return (
    a && b &&
    a.time === b.time &&
    a.open === b.open &&
    a.high === b.high &&
    a.low === b.low &&
    a.close === b.close
  )
}

// computePriceFormat (precision/minMove a partir del precio real de las
// velas, igual que TradingView y la mayoría de charting libs) vive en
// utils/pricePrecision.js — compartida con indicadores que dibujan sus
// propios labels de precio fuera de la serie (rango_quantum, B3) y necesitan
// la MISMA precisión que el eje nativo, no una regla de umbrales propia.

// Fusiona el color por-vela que un indicador (p.ej. supertrend_quantum) haya
// registrado en `candleColorsRef.current`, sin que ese indicador tenga que
// llamar a candleSeries.setData()/update() por su cuenta — ver el comentario
// en la prop `candleColorsRef` más abajo. El ref es un registry con dueño
// único: `{ ownerId: string|null, colorMap: Map<time, {color,borderColor,wickColor}>|null } | null`.
export function getCandleColorMap(candleColorsRef) {
  return candleColorsRef?.current?.colorMap ?? null
}

function applyCandleColor(c, colorMap) {
  const cc = colorMap?.get ? colorMap.get(c.time) : colorMap?.[c.time]
  return cc ? { ...c, ...cc } : { ...c }
}

const PALETTES = {
  classic: { up: '#22c55e', down: '#ef4444' },
  tv:      { up: '#26a69a', down: '#ef5350' },
  blue:    { up: '#3b82f6', down: '#ef4444' },
  mono:    { up: '#94a3b8', down: '#475569' },
  warm:    { up: '#f59e0b', down: '#7c3aed' },
  quantum: { up: '#35A07A', down: '#B4574B' },
  // Velas legibles sobre fondo claro MARFIL (tokens --mf-up/--mf-down) —
  // hoy solo la usa el mini-chart del wizard (EmbeddedPairChart). PALETTES
  // es local al renderer (no se exporta): esta entrada NO aparece en
  // ningún selector del chart principal ni del privado.
  marfil:  { up: '#2E7D5B', down: '#A65A4A' },
}

// Única fuente de verdad de fondos del chart — antes duplicada (con los
// mismos valores, pero drift real de labels) en ChartPage.jsx y en el
// BACKGROUND_LABELS de ChartPublicPage.jsx; ambos importan esto ahora.
export const BACKGROUNDS = {
  default:    { label: 'Auto',      bg: null,      text: null,      grid: null,      isDark: null  },
  dark:       { label: 'Slate',     bg: '#111827', text: '#94a3b8', grid: '#1f2937', isDark: true  },
  charcoal:   { label: 'Carbón',    bg: '#1c1c2e', text: '#8b8baf', grid: '#252542', isDark: true  },
  obsidian:   { label: 'Obsidiana', bg: '#0a0a0a', text: '#6b7280', grid: '#1a1a1a', isDark: true  },
  navy:       { label: 'Navy',      bg: '#0a1628', text: '#7ec8e3', grid: '#0f2035', isDark: true  },
  midnight:   { label: 'Midnight',  bg: '#020817', text: '#475569', grid: '#0f172a', isDark: true  },
  deepBlue:   { label: 'Índigo',    bg: '#0c0f2e', text: '#818cf8', grid: '#14183d', isDark: true  },
  forest:     { label: 'Bosque',    bg: '#0d1f12', text: '#6ee7b7', grid: '#132018', isDark: true  },
  teal:       { label: 'Teal',      bg: '#051c1c', text: '#5eead4', grid: '#0a2828', isDark: true  },
  deepPurple: { label: 'Violeta',   bg: '#0f0a1e', text: '#a78bfa', grid: '#1a1030', isDark: true  },
  deepRed:    { label: 'Burdeos',   bg: '#1a0808', text: '#fca5a5', grid: '#2a1010', isDark: true  },
  copper:     { label: 'Cobre',     bg: '#1a1005', text: '#d97706', grid: '#241808', isDark: true  },
  terminal:   { label: 'Terminal',  bg: '#0d1117', text: '#3fb950', grid: '#161b22', isDark: true  },
  quantum:    { label: 'Quantum',   bg: '#04170F', text: '#F7F5EF', grid: 'rgba(176,141,87,0.07)', isDark: true },
  light:      { label: 'Blanco',    bg: '#ffffff', text: '#64748b', grid: '#f1f5f9', isDark: false },
  cream:      { label: 'Crema',     bg: '#fffbf0', text: '#78716c', grid: '#fef3dc', isDark: false },
  sky:        { label: 'Cielo',     bg: '#f0f7ff', text: '#475569', grid: '#dbeafe', isDark: false },
  sepia:      { label: 'Sepia',     bg: '#f4efe6', text: '#6b5a3e', grid: '#e8dcc8', isDark: false },
}

// Formateadores de tiempo del eje y crosshair. `timeDisplay` puede ser 'local'
// (zona horaria del navegador, default) o 'utc'. Los datos internos siguen en
// UTC; solo cambia la visualización, aplicada en caliente con applyOptions.
//
// Modo UTC: se usan los getters UTC del objeto Date directamente, sin pasar por
// toLocaleString con timeZone: 'UTC', para evitar cualquier interferencia de
// la zona horaria del navegador o de transiciones DST.
//
// Modo LOCAL: se delega en toLocaleString sin explicitar timeZone, de modo que
// el navegador aplica el offset real de la fecha concreta.
const MONTHS_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

function pad2(n) {
  return String(n).padStart(2, '0')
}

function datePartsFor(time, isUtc) {
  let date
  if (typeof time === 'number') {
    date = new Date(time * 1000)
  } else if (typeof time === 'string') {
    date = new Date(time)
  } else if (time && typeof time === 'object') {
    // business-day: interpretar como medianoche en la zona activa para evitar
    // deslizamientos de día cuando el navegador está en una TZ distinta a UTC.
    date = isUtc
      ? new Date(Date.UTC(time.year, time.month - 1, time.day))
      : new Date(time.year, time.month - 1, time.day)
  } else {
    return null
  }
  if (Number.isNaN(date.getTime())) return null
  if (isUtc) {
    return {
      year: date.getUTCFullYear(),
      month: date.getUTCMonth(),
      day: date.getUTCDate(),
      hours: date.getUTCHours(),
      minutes: date.getUTCMinutes(),
    }
  }
  return {
    year: date.getFullYear(),
    month: date.getMonth(),
    day: date.getDate(),
    hours: date.getHours(),
    minutes: date.getMinutes(),
  }
}

export function createTimeFormatters(timeDisplay) {
  const isUtc = timeDisplay === 'utc'

  const timeFormatter = (ts) => {
    if (typeof ts === 'number') {
      if (isUtc) {
        const d = new Date(ts * 1000)
        return `${pad2(d.getUTCDate())} ${MONTHS_SHORT[d.getUTCMonth()]}, ${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`
      }
      return new Date(ts * 1000).toLocaleString('es-ES', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    }
    if (typeof ts === 'string') return ts
    if (ts && typeof ts === 'object') {
      return `${pad2(ts.day)}/${pad2(ts.month)}/${ts.year}`
    }
    return ''
  }

  const dateFormatter = (ts) => {
    if (typeof ts === 'number') {
      if (isUtc) {
        const d = new Date(ts * 1000)
        return `${pad2(d.getUTCDate())} ${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`
      }
      return new Date(ts * 1000).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })
    }
    if (typeof ts === 'string') return ts
    if (ts && typeof ts === 'object') {
      return `${pad2(ts.day)} ${MONTHS_SHORT[ts.month - 1]} ${ts.year}`
    }
    return ''
  }

  const tickMarkFormatter = (time, tickMarkType) => {
    const parts = datePartsFor(time, isUtc)
    if (!parts) return ''
    if (tickMarkType === TickMarkType.Time) {
      return `${pad2(parts.hours)}:${pad2(parts.minutes)}`
    }
    return `${parts.day} ${MONTHS_SHORT[parts.month]}`
  }

  return { timeFormatter, dateFormatter, tickMarkFormatter }
}

export default function ChartRenderer({
  data,
  dataRef,
  subscribe,
  dataVersion,
  chartType,
  palette,
  chartBg,
  // Override directo de fondo ({ bg, text, grid }) que GOBIERNA sobre
  // BACKGROUNDS[chartBg] cuando viene — para consumidores que necesitan un
  // fondo fuera del registro SIN añadirlo a los selectores del chart
  // (hoy solo el mini-chart del wizard en clave MARFIL, EmbeddedPairChart).
  bgOverride,
  isDark,
  tradingMode,
  symbol,
  timeframe,
  onChartReady,
  onCrosshairMove,
  onClick,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  disableRealtimeUpdates,
  onMouseLeave,
  // Coloreado de velas por indicador (p.ej. supertrend_quantum) — un
  // ref registry con dueño único:
  //   { current: { ownerId: string|null, colorMap: Map<time, {color,borderColor,wickColor}>|null } | null }
  // y un contador que se sube cada vez que ese registry cambia. ChartRenderer
  // es el ÚNICO sitio que llama a candleSeries.setData()/update() (tanto en
  // el efecto de datos como en el de color y en el de ticks en vivo); antes un
  // indicador (supertrend_quantum) llamaba a setData() directamente desde su
  // propio render(), compitiendo por la misma serie con este componente y
  // con el tick en vivo — esa carrera es la que producía "Value is null"
  // diferido de forma intermitente. Con este canal, el indicador solo escribe
  // en el ref y pide un repintado; ChartRenderer decide cuándo y con qué
  // datos reconstruir la serie, como con cualquier otro cambio. La liberación
  // es owner-aware: solo el dueño (o las páginas cuando el dueño se desactiva
  // o se va a repintar) puede poner el ref a null.
  candleColorsRef,
  colorVersion,
  enableVertTouchDrag = false,
  timeDisplay = 'local',
  // Vela en formación en tiempo real (canal en vivo del chart público, ver
  // useLiveCandle.js). Viaja FUERA de `data`/`dataRef` a propósito: así no
  // re-dispara los efectos de indicadores de la página (que calculan sobre
  // velas cerradas). Se aplica con series.update() incremental en un efecto
  // dedicado más abajo; la reconciliación con el histórico la sigue haciendo
  // el efecto de datos cuando llega el poll REST. Cada vela viene etiquetada
  // con { symbol, timeframe } de la suscripción que la produjo — el efecto la
  // descarta si no coincide con el símbolo/TF vigente o si hay una transición
  // de símbolo/TF en curso (transitioningRef).
  liveCandle = null,
  // Prefijo de la key localStorage donde se persiste el rango visible
  // (zoom del usuario). El chart principal usa el default
  // 'chart_visible_range'; el mini-chart embebido del wizard pasa uno propio
  // ('wizard_visible_range') para NO compartir el zoom guardado del chart
  // principal del mismo símbolo/TF.
  rangeStorageKeyPrefix = 'chart_visible_range',
  // Líneas de precio aditivas (cockpit de posición, Fase 5.0 — diseño
  // docs/ux/cockpit-posicion.md §8): misma doctrina que bgOverride — prop
  // opcional que no cambia nada cuando no viene. Array MEMOIZADO por el
  // consumidor de descriptores planos:
  //   [{ price, color, title?, lineWidth?, lineStyle?, axisLabelVisible? }]
  // Se dibujan con series.createPriceLine (etiqueta EN EL EJE, patrón de
  // ICTCanvas.jsx:104-162) y se rehacen enteras cuando cambia la prop
  // (removePriceLine de las anteriores primero).
  priceLines = null,
  // Auto-encaje de las priceLines (lote cockpit-polish 2026-09-01, fix del
  // chart "chafado" del cockpit): null (default) = comportamiento previo,
  // TODAS las líneas fuerzan el rango del autoscale expand-only. Con un %
  // (p.ej. LEVEL_AUTOFIT_MAX_PCT del cockpit) solo fuerzan el rango las
  // líneas a esa distancia o menos del precio actual (última vela de la
  // serie) — las demás se dibujan igual pero ya no aplastan las velas.
  priceLinesFitMaxPct = null,
  // Fragmento extra de la reset-key del autoscale estable: al cambiar (el
  // cockpit lo conmuta con su toggle "Ver todos los niveles") el ancla
  // expand-only se descarta y se reconstruye desde las velas visibles —
  // es lo que permite ENCOGER el rango al volver al encaje cercano (el
  // ancla por sí sola es expand-only y jamás encogería).
  priceLinesFitKey = '',
  // Marcadores sobre velas (revisión de trade cerrado del cockpit):
  // descriptores de series.setMarkers() de lightweight-charts
  // ([{ time, position, color, shape, text? }], time = time de una vela del
  // dataset). También memoizados por el consumidor; el efecto re-aplica con
  // setMarkers([]) cuando la prop se vacía. NO se exponen vía onChartReady:
  // ChartRenderer es el único que escribe en la serie principal (doctrina
  // del dueño único de applyMainSetData) y los markers son escritura sobre
  // esa misma serie — pasar la instancia a la página reabriría la carrera.
  markers = null,
}) {
  const containerRef = useRef(null)
  const chartRef     = useRef(null)
  const seriesRef    = useRef(null)
  const chartTypeRef = useRef(chartType)
  const ignoreRangeChangeRef = useRef(false)
  const rangeKeyRef = useRef(`${rangeStorageKeyPrefix}_${symbol || 'default'}`)
  const initialRestoreDoneRef = useRef(false)
  // true desde el mount / cambio de símbolo-TF hasta que se aplica el rango
  // inicial (restaurado o por defecto). Mientras tanto NO se persiste ningún
  // cambio de rango: los que emite lightweight-charts al recibir los datos
  // (setData) no son zoom del usuario, y antes se guardaban encima del zoom
  // real justo antes de que la restauración lo leyera — el zoom guardado no
  // se restauraba nunca (bug encontrado 2026-10-22).
  const restorePendingRef = useRef(true)
  const prevTimeframeRef = useRef(timeframe)
  const prevSymbolRef = useRef(symbol)
  // true tras un cambio de símbolo hasta que el efecto de datos recibe las
  // velas del nuevo (ver el guard en el efecto de datos).
  const awaitingNewSymbolDataRef = useRef(false)
  const forceLatestRef = useRef(false)
  // Referencia a los datos vigentes en el momento de un cambio de símbolo/
  // timeframe (ver más abajo por qué es necesaria).
  const dataAtResetRef = useRef(null)
  // Snapshot de los datos ya aplicados a la serie principal; permite detectar
  // refrescos de polling que solo añaden/modifican la última vela y usar
  // series.update() en vez de setData(), evitando el reset del time scale
  // (parpadeo/blanco) en cada poll.
  const prevDataRef = useRef(null)
  // Instancias de price line vivas creadas por la prop aditiva `priceLines`
  // (cockpit, Fase 5.0) — el efecto dedicado las retira antes de crear las
  // nuevas y el cleanup del mount destruye el chart entero (no hace falta
  // cleanup propio al desmontar).
  const priceLinesRef = useRef([])
  // Min/max de las priceLines vivas de la prop aditiva (cockpit): lo lee el
  // autoscale estable vía getExtraRange para encuadrar los niveles del
  // trade (lwc no las incluye en el autoscale). Con priceLinesFitMaxPct
  // distinto de null solo entran las líneas dentro del umbral del precio
  // actual — ver la prop.
  const priceLineRangeRef = useRef(null)
  // Ref de la prop priceLinesFitKey para que el provider del autoscale
  // (closure del efecto de mount) lea siempre el valor vigente.
  const priceLinesFitKeyRef = useRef(priceLinesFitKey)
  // true cuando el filtro de auto-encaje está realmente ACTIVO (hay
  // priceLinesFitMaxPct y ya existe precio de referencia = última vela).
  // Forma parte de la reset-key del autoscale: las priceLines suelen llegar
  // ANTES que las velas (modal del cockpit), y sin este flag el ancla
  // expand-only se construiría con el rango completo de niveles (filtro
  // inerte sin referencia) y ya no encogería nunca — el chart "chafado"
  // de vuelta. Al aterrizar las velas el flag cambia y el ancla se
  // reconstruye con el rango ya filtrado.
  const priceLineFitActiveRef = useRef(false)
  // Último `time` de vela en vivo aplicado a la serie — evita re-aplicar
  // ticks fuera de orden o restos del stream de un símbolo/timeframe anterior.
  const lastLiveTimeRef = useRef(null)
  // Transición de símbolo/timeframe en curso: true desde el mount o un cambio
  // de símbolo/TF hasta que el efecto de datos aplica el setData del histórico
  // nuevo. Mientras tanto NO hay última vela válida y series.update() es
  // ilegal — el efecto del canal en vivo descarta todos los ticks. Es la
  // lección del bug STQ aplicada al canal en vivo: la garantía es el ORDEN
  // (desuscribir viejo → setData histórico → aceptar ticks), no el try/catch.
  const transitioningRef = useRef(true)
  // Referencia a los datos vigentes en el momento de un cambio de símbolo/TF
  // (null en el mount): el "tick" síncrono de carga re-ejecuta el efecto de
  // datos con los datos TODAVÍA viejos, y esa aplicación NO debe cerrar la
  // transición. Solo los datos nuevos (identidad distinta) la cierran.
  const dataAtSwitchRef = useRef(null)
  // DIAG TEMPORAL (P0 blanco prod, 28/07): motivos de descarte ya logueados
  // (los ticks llegan cada segundo — se loguea UNA vez por motivo/símbolo).
  // Quitar tras el diagnóstico.
  const liveDiscardLoggedRef = useRef(new Set())
  // La serie principal PINTADA: la instancia creada en el efecto de mount y
  // entregada a la página por onChartReady. Solo el mount la escribe — es la
  // referencia de verdad de la instrumentación de identidad (punto 3).
  const mountedSeriesRef = useRef(null)
  // Época de la instancia de la serie principal: sube cada vez que el mount
  // (re)crea la serie. El efecto de datos depende de ella (punto 2): si algún
  // path legítimo recrea la serie (remount por key={chartType}), el efecto se
  // re-ejecuta y re-aplica el setData sobre la instancia NUEVA.
  const [seriesEpoch, setSeriesEpoch] = useState(0)

  // ÚNICA vía de escritura completa de la serie principal (puntos 1 y 3):
  // - La referencia se lee de seriesRef.current EN EL MOMENTO DE LA LLAMADA —
  //   nunca una local capturada de un closure (el setData de SOL fue a una
  //   serie desmontada por una referencia vieja en closure; setData sobre una
  //   serie desmontada no lanza, de ahí el blanco sin excepción).
  // - Cada setData loguea su identidad en una línea por el logger gateado:
  //   instancia que lo recibe vs serie pintada. Si vuelve a pasar, la traza
  //   lo dice en una línea (visible en prod solo con el modo debug activo).
  const applyMainSetData = (rows, origen) => {
    const live = seriesRef.current
    const pintada = mountedSeriesRef.current
    const identidadOk = !!live && live === pintada
    debugWarn('[CHART-GUARD] setData identidad:', {
      origen, symbol, timeframe,
      instancia: live?.__mainSeriesId ?? null,
      pintada: pintada?.__mainSeriesId ?? null,
      identidadOk,
      velas: rows?.length ?? 0,
    })
    if (!identidadOk) return false
    live.setData(rows)
    return true
  }

  chartTypeRef.current = chartType
  rangeKeyRef.current = `${rangeStorageKeyPrefix}_${(symbol || 'default').replace(/[^a-zA-Z0-9]/g, '_')}_${timeframe || '1h'}`

  useEffect(() => {
    initialRestoreDoneRef.current = false
    restorePendingRef.current = true
    // Los ticks en vivo del símbolo/timeframe anterior no valen aquí.
    lastLiveTimeRef.current = null
    // Cambio real de símbolo/TF (no el mount): se abre la transición — ningún
    // tick en vivo se aplica hasta que el efecto de datos complete el setData
    // del histórico nuevo (ver transitioningRef arriba).
    if (symbol !== prevSymbolRef.current || timeframe !== prevTimeframeRef.current) {
      transitioningRef.current = true
      dataAtSwitchRef.current = dataRef?.current ?? data ?? null
    }
    // useChartData.load() emite un "tick" SÍNCRONO al empezar a cargar
    // (antes de que la petición resuelva) para que la UI pueda mostrar el
    // spinner de carga. Ese tick dispara el efecto de más abajo (setData)
    // con los datos TODAVÍA del timeframe/símbolo anterior. Si no
    // distinguimos ese disparo "viejo" del disparo real (cuando llegan los
    // datos nuevos), el restablecimiento de zoom consume su única
    // oportunidad (initialRestoreDoneRef) contra datos obsoletos y nunca se
    // vuelve a ejecutar cuando llegan los datos correctos — dejando el rango
    // visible de lightweight-charts desalineado con la serie de velas real,
    // lo que dispara "Value is null" de forma diferida al intentar pintar
    // barras en índices que ya no existen. Guardamos aquí la referencia de
    // los datos "viejos" para poder ignorarlos en el efecto de abajo.
    dataAtResetRef.current = dataRef?.current ?? data ?? null
    // A timeframe switch should always land on the current price instead of
    // restoring wherever the user had panned to the last time they viewed
    // that timeframe — that's what made switching timeframes feel like it
    // required manually scrolling back to "now" every time. A symbol switch
    // (same timeframe) still restores its own saved position as before.
    forceLatestRef.current = timeframe !== prevTimeframeRef.current
    prevTimeframeRef.current = timeframe
    // Cambio de SÍMBOLO: las velas del símbolo anterior no significan nada
    // para el nuevo — se retiran de inmediato. Sin este reset, la transición
    // mostraba estado mezclado (ticker del símbolo nuevo, velas/eje del
    // viejo) y, si la carga se quedaba a medias por cualquier fallo, el eje
    // conservaba el rango del símbolo anterior indefinidamente sin camino de
    // recuperación. Con la serie vacía, el price scale suelta el rango viejo
    // (autoscale sin datos) y el efecto de datos hace setData completo +
    // encuadre cuando llegan las velas nuevas. prevDataRef a null fuerza ese
    // setData completo (nunca incremental entre símbolos, aunque compartan
    // rejilla de tiempos). Solo en cambio de símbolo: en cambio de timeframe
    // se mantiene el comportamiento suave (datos viejos pintados hasta que
    // llegan los nuevos).
    if (symbol !== prevSymbolRef.current) {
      prevSymbolRef.current = symbol
      prevDataRef.current = null
      // Mientras no lleguen las velas del símbolo nuevo, el efecto de datos
      // no debe repintar las del viejo (el "tick" síncrono de carga las
      // traería de vuelta justo después de haberlas retirado).
      awaitingNewSymbolDataRef.current = true
      try { applyMainSetData([], 'symbol-switch-clear') } catch {}
      debugLog('[CHART-GUARD] renderer: cambio de símbolo — serie vaciada, esperando velas nuevas:', { symbol, timeframe })
    }
  }, [symbol, timeframe])

  const pal = PALETTES[palette] || PALETTES.classic
  const bg  = bgOverride || BACKGROUNDS[chartBg] || BACKGROUNDS.default

  useEffect(() => {
    if (!containerRef.current) return

    const chart = createChart(containerRef.current, {
      layout: {
        background: { color: bg.bg || (isDark ? '#111827' : '#ffffff') },
        textColor:  bg.text || (isDark ? '#94a3b8' : '#64748b'),
      },
      grid: {
        vertLines: { color: bg.grid || (isDark ? '#1f2937' : '#f1f5f9') },
        horzLines: { color: bg.grid || (isDark ? '#1f2937' : '#f1f5f9') },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
      },
      rightPriceScale: {
        borderColor: bg.grid || (isDark ? '#1f2937' : '#f1f5f9'),
      },
      timeScale: {
        borderColor: bg.grid || (isDark ? '#1f2937' : '#f1f5f9'),
        timeVisible: true,
        secondsVisible: false,
        barSpacing: 8,
        minBarSpacing: 0.5,
        // rightOffset amplio: la caja de señal ICT (entry→SL/TP) se extiende
        // ~20 barras a futuro; con 5 de offset la caja quedaba comprimida en
        // un sliver de ~40px pegado al eje de precio.
        rightOffset: RIGHT_OFFSET_BARS,
        fixLeftEdge: false,
        fixRightEdge: false,
        shiftVisibleRangeOnNewBar: true,
        tickMarkFormatter: createTimeFormatters(timeDisplay).tickMarkFormatter,
      },
      localization: {
        // lightweight-charts v4 espera timestamps UTC en segundos. El toggle
        // LOCAL/UTC solo cambia la visualización del eje, crosshair y tooltips
        // (timeZone en los formateadores); los datos internos siguen en UTC.
        timeFormatter: createTimeFormatters(timeDisplay).timeFormatter,
        dateFormatter: createTimeFormatters(timeDisplay).dateFormatter,
      },
      // La librería captura por defecto (true) los deslizamientos verticales
      // de un dedo dentro del área del chart; ChartPage (privada, con más
      // contenido debajo del chart en la página) lo desactiva desde siempre
      // para no robarle ese gesto al scroll de la página. El chart público
      // en móvil no tiene scroll de página bajo el chart (layout fijo a
      // pantalla completa) — mantenerlo desactivado ahí, con `touchAction:
      // 'none'` puesto más abajo, hacía que cualquier componente vertical de
      // un gesto (un paneo no perfectamente horizontal, o deslizar sobre la
      // columna de precio) no lo capturara ni el chart ni el navegador: se
      // perdía sin más, y por eso el paneo se sentía "atascado" en vez de
      // fluido.
      handleScroll: { vertTouchDrag: enableVertTouchDrag },
      handleScale: { axisPressedMouseMove: true },
      width: containerRef.current.clientWidth,
      height: containerRef.current.clientHeight,
    })

    chartRef.current = chart

    let series
    if (chartType === 'candles') {
      series = chart.addCandlestickSeries({
        upColor: pal.up, downColor: pal.down, borderVisible: false,
        wickUpColor: pal.up, wickDownColor: pal.down,
        crosshairMarkerVisible: false,
      })
    } else if (chartType === 'line') {
      series = chart.addLineSeries({ color: pal.up, lineWidth: 2 })
    } else if (chartType === 'area') {
      series = chart.addAreaSeries({
        topColor: pal.up, bottomColor: pal.up + '20',
        lineColor: pal.up, lineWidth: 2,
      })
    } else {
      series = chart.addCandlestickSeries({
        upColor: pal.up, downColor: pal.down, borderVisible: false,
        wickUpColor: pal.up, wickDownColor: pal.down,
      })
    }
    seriesRef.current = series

    // Punto 2 — dueño único de la serie principal: el registry de indicadores
    // (ciclo añadir/quitar STQ) NO la recrea ni la desmonta jamás. Se marca la
    // instancia y se envuelve chart.removeSeries en el propio chart, de modo
    // que NINGÚN consumidor (registry, overlays MA/RSI/volumen, drawing tools)
    // pueda desmontarla aunque lo intente — queda bloqueado y logueado.
    series.__isMainSeries = true
    series.__mainSeriesId = nextMainSeriesId++
    mountedSeriesRef.current = series
    if (!chart.__mainSeriesRemoveGuard && typeof chart.removeSeries === 'function') {
      chart.__mainSeriesRemoveGuard = true
      const boundRemoveSeries = chart.removeSeries.bind(chart)
      chart.removeSeries = (s) => {
        if (s?.__isMainSeries) {
          debugWarn('[CHART-GUARD] removeSeries sobre la serie principal BLOQUEADO (dueño único):', { instancia: s.__mainSeriesId ?? null })
          return
        }
        return boundRemoveSeries(s)
      }
    }
    // Punto 5 — autoscale estable en vivo: la escala de precio no se re-ajusta
    // por tick; solo expande si el precio vivo sale del rango visible y nunca
    // encoge en vivo (ver createStableLiveAutoscale arriba).
    series.applyOptions({
      autoscaleInfoProvider: createStableLiveAutoscale({
        getVisibleLogicalRange: () => {
          try { return chartRef.current?.timeScale().getVisibleLogicalRange() ?? null } catch { return null }
        },
        getDataLength: () => {
          try { return seriesRef.current?.data?.().length ?? 0 } catch { return 0 }
        },
        // El ancla no puede sobrevivir a un cambio de símbolo/timeframe
        // (rangeKeyRef incluye ambos) ni a un cambio del modo de encaje de
        // niveles del cockpit (priceLinesFitKey — ver la prop): en ambos
        // casos se reconstruye desde las velas visibles.
        getResetKey: () => `${rangeKeyRef.current}|${priceLinesFitKeyRef.current}|${priceLineFitActiveRef.current ? 'f' : ''}`,
        // Durante la transición de símbolo/TF (serie vaciada, histórico nuevo
        // aún no aplicado) no se ancla: autoscale natural.
        isTransitioning: () => transitioningRef.current,
        // Cockpit: encuadra los niveles de las priceLines (entrada/SL/TPs)
        // en el autoscale expand-only — ver la factory.
        getExtraRange: () => priceLineRangeRef.current,
      }),
    })
    // La instancia nueva arranca sin datos: fuerza setData completo (nunca
    // incremental) en la próxima pasada del efecto de datos.
    prevDataRef.current = null
    setSeriesEpoch(e => e + 1)
    // La creación se loguea por el logger gateado (modo debug); la traza
    // permanente es la de identidad de cada setData (applyMainSetData), que
    // incluye instancia y pintada — con eso basta para detectar la divergencia.
    debugLog('[CHART-GUARD] serie principal CREADA:', { instancia: series.__mainSeriesId, chartType })

    if (onChartReady) onChartReady(chart, series)

    chart.subscribeCrosshairMove((param) => {
      if (!param?.point || !param.time || !series) return
      const data = param.seriesData.get(series)
      if (data && onCrosshairMove) {
        onCrosshairMove({ time: param.time, price: data.value ?? data.close })
      }
    })

    chart.subscribeClick((param) => {
      if (!param?.point || !param.time || !series) return
      const data = param.seriesData.get(series)
      if (data && onClick) {
        onClick({ time: param.time, price: data.value ?? data.close })
      }
    })

    const ro = new ResizeObserver(() => {
      if (!containerRef.current || !chartRef.current) return
      chartRef.current.applyOptions({
        width: containerRef.current.clientWidth,
        height: containerRef.current.clientHeight,
      })
    })
    ro.observe(containerRef.current)

    // Safari en iOS ignora `user-scalable=no`/`maximum-scale` del meta
    // viewport para el gesto de pellizco desde iOS 10 (lo trata como zoom
    // de accesibilidad) y lo captura como zoom de página en vez de
    // dejárselo al canvas — competía con el pinch-to-zoom propio de
    // lightweight-charts y hacía el gesto tosco/inconsistente. `gesturestart`
    // /`gesturechange`/`gestureend` son eventos propios de WebKit para este
    // gesto; sin `{ passive: false }` el preventDefault no tiene efecto.
    const preventNativeGesture = (e) => e.preventDefault()
    const el = containerRef.current
    el.addEventListener('gesturestart', preventNativeGesture, { passive: false })
    el.addEventListener('gesturechange', preventNativeGesture, { passive: false })
    el.addEventListener('gestureend', preventNativeGesture, { passive: false })

    return () => {
      ro.disconnect()
      el.removeEventListener('gesturestart', preventNativeGesture)
      el.removeEventListener('gesturechange', preventNativeGesture)
      el.removeEventListener('gestureend', preventNativeGesture)
      chart.remove()
      chartRef.current = null
      seriesRef.current = null
      mountedSeriesRef.current = null
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Prop aditiva `priceLines` (cockpit de posición, Fase 5.0): rehace las
  // líneas de precio cuando cambia el array (memoizado por el consumidor) o
  // la INSTANCIA de la serie (seriesEpoch — tras un remount por key=
  // {chartType} las líneas hay que recrearlas sobre la serie nueva).
  // Lectura de seriesRef.current EN EL MOMENTO de ejecutarse, nunca una
  // local de closure (misma lección que applyMainSetData).
  useEffect(() => {
    const series = seriesRef.current
    if (!series) return
    for (const pl of priceLinesRef.current) {
      try { series.removePriceLine(pl) } catch {}
    }
    priceLinesRef.current = []
    if (!Array.isArray(priceLines)) {
      priceLineRangeRef.current = null
      priceLineFitActiveRef.current = false
      return
    }
    let minLine = Infinity
    let maxLine = -Infinity
    // Referencia del umbral de auto-encaje (priceLinesFitMaxPct): el precio
    // actual = cierre de la última vela. Se lee de la PROP de datos (misma
    // resolución que el efecto de datos: dataRef antes que data), NUNCA de
    // series.data() — este efecto se declara ANTES que el de datos, así que
    // en el commit en que llegan las velas la serie todavía está vacía y la
    // referencia saldría null (filtro inerte → chart "chafado" otra vez).
    let fitRef = null
    if (priceLinesFitMaxPct != null) {
      try {
        const src = dataRef?.current ?? data
        const lastBar = Array.isArray(src) && src.length ? src[src.length - 1] : null
        const rc = lastBar?.close ?? lastBar?.value
        if (Number.isFinite(rc) && rc > 0) fitRef = rc
      } catch { fitRef = null }
    }
    priceLineFitActiveRef.current = priceLinesFitMaxPct != null && fitRef !== null
    for (const cfg of priceLines) {
      if (!cfg || cfg.price == null || !Number.isFinite(Number(cfg.price))) continue
      const p = Number(cfg.price)
      // La línea se DIBUJA siempre; solo entra en el rango del autoscale si
      // supera el filtro de distancia (o no hay filtro/referencia).
      const fitsRange = priceLinesFitMaxPct == null || fitRef === null
        || (Math.abs(p - fitRef) / fitRef) * 100 <= priceLinesFitMaxPct
      if (fitsRange) {
        minLine = Math.min(minLine, p)
        maxLine = Math.max(maxLine, p)
      }
      try {
        const color = cfg.color || '#94a3b8'
        priceLinesRef.current.push(series.createPriceLine({
          price: p,
          color,
          lineWidth: cfg.lineWidth ?? 1,
          lineStyle: cfg.lineStyle ?? LineStyle.Solid,
          axisLabelVisible: cfg.axisLabelVisible ?? true,
          title: cfg.title ?? '',
          axisLabelColor: color,
          axisLabelTextColor: getContrastColor(color),
        }))
      } catch {}
    }
    priceLineRangeRef.current = minLine <= maxLine ? { min: minLine, max: maxLine } : null
    // Re-aplicar autoScale fuerza el recálculo del rango AHORA (las líneas
    // pueden llegar después del setData inicial — p.ej. la revisión de
    // cerrado, que espera el fetch de eventos): sin este poke el encuadre
    // de los niveles esperaría al próximo cambio de datos.
    try { chartRef.current?.priceScale('right').applyOptions({ autoScale: true }) } catch {}
    // dataVersion en deps: las velas pueden llegar DESPUÉS que las líneas
    // (cockpit) — hace falta recomputar la referencia del filtro con la
    // serie ya poblada (ver priceLineFitActiveRef).
  }, [priceLines, seriesEpoch, priceLinesFitMaxPct, dataVersion])

  // Prop aditiva `priceLinesFitKey` (toggle "Ver todos los niveles" del
  // cockpit): actualiza la ref que compone la reset-key del autoscale
  // estable y pega el poke que fuerza el recálculo AHORA con el ancla ya
  // descartada — sin el poke, el re-encaje (expandir o encoger) esperaría
  // al próximo cambio de datos.
  useEffect(() => {
    priceLinesFitKeyRef.current = priceLinesFitKey
    try { chartRef.current?.priceScale('right').applyOptions({ autoScale: true }) } catch {}
  }, [priceLinesFitKey])

  // Prop aditiva `markers` (revisión de trade cerrado del cockpit): espejo
  // de la de priceLines con series.setMarkers(). Sin cleanup al desmontar:
  // el cleanup del mount destruye el chart con sus marcadores.
  useEffect(() => {
    const series = seriesRef.current
    if (!series) return
    try { series.setMarkers(Array.isArray(markers) ? markers : []) } catch {}
  }, [markers, seriesEpoch])

  // Actualiza la serie principal. Solo depende de los datos (símbolo,
  // timeframe, respuesta del fetch), del tipo de vela y de la INSTANCIA de la
  // serie (seriesEpoch — punto 2: si un path legítimo la recrea, este efecto
  // se re-ejecuta y re-aplica el setData sobre la nueva) — nunca de toggles de
  // overlays o del mapa de colores de velas. Para refrescos de polling que
  // solo modifican/añaden la última vela usamos series.update(), que no
  // resetea el time scale; setData() completo se reserva para carga inicial,
  // cambio de símbolo/timeframe o cambio de tipo de vela. TODA escritura
  // completa pasa por applyMainSetData (referencia viva + log de identidad,
  // puntos 1 y 3); las locales `series`/`chart` de este efecto solo se usan de
  // forma SÍNCRONA dentro de la pasada — los callbacks diferidos (el timeout
  // del restore de zoom) releen chartRef.current en el momento de ejecutarse.
  useEffect(() => {
    const series = seriesRef.current
    const chart = chartRef.current
    if (!series || !chart) {
      debugLog('[ChartRenderer data effect] skipped: no series/chart', { series: !!series, chart: !!chart })
      return
    }

    const currentData = dataRef?.current ?? data ?? []
    if (!currentData || currentData.length === 0) {
      return
    }

    // Tras un cambio de símbolo, los datos del símbolo anterior no se
    // repintan: la serie quedó vacía a propósito (efecto [symbol,timeframe])
    // y solo se vuelve a pintar cuando llegan velas distintas a las
    // capturadas en el reset (identidad de array — las "viejas" son la misma
    // referencia que dataAtResetRef).
    if (awaitingNewSymbolDataRef.current) {
      if (currentData === dataAtResetRef.current) {
        debugLog('[CHART-GUARD] renderer: velas del símbolo ANTERIOR bloqueadas (esperando las nuevas):', { symbol, velas: currentData.length, dataVersion })
        return
      }
      awaitingNewSymbolDataRef.current = false
    }

    const sanitized = sanitizeCandles(currentData)
    if (sanitized.length === 0) {
      debugWarn('[ChartRenderer data effect] velas descartadas por sanitize (0 útiles):', { symbol, recibidas: currentData.length, muestra: currentData[0] ?? null })
      return
    }

    const previousData = prevDataRef.current
    let isIncremental = false
    if (previousData && previousData.length > 0 && sanitized.length >= previousData.length) {
      // Mismo primera vela -> mismo dataset; solo la cola puede haber cambiado.
      if (sanitized[0].time === previousData[0].time) {
        const sameLength = sanitized.length === previousData.length
        const addedOne = sanitized.length === previousData.length + 1
        if (sameLength || addedOne) {
          let mismatch = false
          const prevLastIdx = previousData.length - 1
          // Comparar velas anteriores a la última del dataset previo. En el caso
          // `addedOne` la anterior última vela ahora es la penúltima y puede haber
          // cambiado de valor al cerrarse; sólo exigimos que su tiempo coincida.
          const compareLimit = addedOne ? prevLastIdx - 1 : prevLastIdx
          for (let i = 0; i < compareLimit; i++) {
            if (!sameCandle(sanitized[i], previousData[i])) {
              mismatch = true
              break
            }
          }
          if (!mismatch && addedOne && previousData[prevLastIdx].time !== sanitized[prevLastIdx].time) {
            mismatch = true
          }
          isIncremental = !mismatch
        }
      }
    }

    // update() incremental solo es legal sobre la última vela REAL de la
    // serie. Si el canal en vivo ya añadió una vela futura (cambio de vela en
    // vivo antes de que el poll REST la confirme), la serie va por delante de
    // los datos históricos y un update() con time anterior lanzaría "Cannot
    // update oldest data" sin capturar — se resincroniza con setData completo.
    if (isIncremental) {
      const seriesBars = series.data()
      const seriesLastTime = seriesBars.length > 0 ? seriesBars[seriesBars.length - 1].time : null
      if (seriesLastTime != null && sanitized[sanitized.length - 1].time < seriesLastTime) {
        isIncremental = false
      }
    }

    if (isIncremental) {
      const prevLastIdx = previousData.length - 1
      const addedOne = sanitized.length === previousData.length + 1
      debugLog('[ChartRenderer data effect] incremental update', { dataVersion, addedOne, prevLastIdx, seriesDataLen: series.data().length })
      series.applyOptions({ priceFormat: computePriceFormat(sanitized) })
      if (chartType === 'candles') {
        if (addedOne) {
          series.update(applyCandleColor(sanitized[prevLastIdx], getCandleColorMap(candleColorsRef)))
        }
        series.update(applyCandleColor(sanitized[sanitized.length - 1], getCandleColorMap(candleColorsRef)))
      } else {
        if (addedOne) {
          series.update({ time: sanitized[prevLastIdx].time, value: sanitized[prevLastIdx].close })
        }
        series.update({ time: sanitized[sanitized.length - 1].time, value: sanitized[sanitized.length - 1].close })
      }
      prevDataRef.current = sanitized
      // Datos NUEVOS aplicados (no el disparo "viejo" del cambio de símbolo/
      // TF): se cierra la transición y el canal en vivo vuelve a aceptar
      // ticks — el orden garantizado es setData histórico → aceptar ticks.
      if (currentData !== dataAtSwitchRef.current) {
        transitioningRef.current = false
        dataAtSwitchRef.current = null
      }
      return
    }

    debugLog('[ChartRenderer data effect] full setData', { dataVersion, currentDataLen: currentData.length, sanitizedLen: sanitized.length, seriesDataLen: series.data().length, chartType })
    series.applyOptions({ priceFormat: computePriceFormat(sanitized) })
    let applied = false
    if (chartType === 'candles') {
      if (sanitized.some(c => ![c.open, c.high, c.low, c.close].every(Number.isFinite))) {
        debugError('[ChartRenderer data effect] invalid OHLC detected, skipping setData')
        return
      }
      applied = applyMainSetData(sanitized.map(c => applyCandleColor(c, getCandleColorMap(candleColorsRef))), 'data-effect-full')
    } else {
      if (sanitized.some(c => !Number.isFinite(c.close))) {
        debugError('[ChartRenderer data effect] invalid close detected, skipping setData')
        return
      }
      applied = applyMainSetData(sanitized.map(c => ({ time: c.time, value: c.close })), 'data-effect-full')
    }
    // Si la identidad no era la de la serie pintada no se escribió nada: no
    // tocar prevDataRef ni cerrar la transición (el log de identidad de arriba
    // ya dejó la línea que lo delata).
    if (!applied) return
    prevDataRef.current = sanitized
    // Igual que en el path incremental: histórico nuevo ya en la serie, se
    // cierra la transición y se vuelven a aceptar ticks en vivo.
    if (currentData !== dataAtSwitchRef.current) {
      transitioningRef.current = false
      dataAtSwitchRef.current = null
    }

    // Restore saved zoom only once per symbol/timeframe (or on first data load).
    // Skipped entirely right after a timeframe switch so the chart always
    // lands on the current price instead of wherever it was last panned to.
    //
    // `currentData !== dataAtResetRef.current` descarta el disparo "viejo":
    // useChartData.load() bumpea dataVersion de forma síncrona al empezar a
    // cargar (para el spinner) usando todavía los datos del timeframe
    // anterior, y sólo vuelve a bumpearlo cuando la petición resuelve con los
    // datos reales. Sin este chequeo, ese primer disparo "gastaba" el único
    // restablecimiento de zoom contra datos obsoletos y dejaba el rango
    // visible desalineado con la serie real una vez llegaban los datos
    // correctos (causa de un "Value is null" diferido de lightweight-charts).
    if (currentData.length > 0 && !initialRestoreDoneRef.current && currentData !== dataAtResetRef.current) {
      initialRestoreDoneRef.current = true
      const skipRestore = forceLatestRef.current
      forceLatestRef.current = false
      // Si el chart se desmonta antes de que este timeout llegue a disparar
      // (p. ej. `key={chartType}` en la página forzando un remount al
      // cambiar de tipo de vela justo después de un cambio de símbolo/
      // timeframe/tipo previo, dentro del mismo tick), `chart` queda
      // referenciando una instancia ya destruida — por eso aquí NO se usa la
      // local del closure: se relee chartRef.current EN EL MOMENTO de
      // ejecutarse (punto 1) y se aborta si ya no hay chart vivo.
      const restoreTimeoutId = setTimeout(() => {
        const liveChart = chartRef.current
        if (!liveChart) return
        const ts = liveChart.timeScale()
        const key = rangeKeyRef.current
        // Antes de aplicar el rango: el evento que emite setVisibleLogicalRange
        // lo consume ignoreRangeChangeRef, y desde aquí los gestos del usuario
        // ya se persisten.
        restorePendingRef.current = false
        let restored = false
        if (!skipRestore) {
          try {
            const raw = localStorage.getItem(key)
            if (raw) {
              const range = decodeSavedRange(JSON.parse(raw), sanitized.length - 1)
              if (range) {
                ignoreRangeChangeRef.current = true
                ts.setVisibleLogicalRange(range)
                restored = true
              }
            }
          } catch {}
        }
        if (!restored) {
          ignoreRangeChangeRef.current = true
          const count = Math.min(350, sanitized.length)
          ts.setVisibleLogicalRange(initialVisibleRange(sanitized.length, count))
        }
        // Fix blanco BTC/ETH/SOL/1000PEPE prod (2026-07-29, validado): el anchor
        // de createStableLiveAutoscale es expand-only por diseño (nunca encoge),
        // y ni series.setData() ni setVisibleLogicalRange() invalidan por sí
        // solos el priceRange que lightweight-charts ya tenía cacheado del
        // símbolo anterior — el eje de precio se quedaba con los bounds viejos
        // aunque la serie ya tuviera las velas correctas. applyOptions con el
        // mismo valor (autoScale ya es true) fuerza igualmente un fullUpdate()
        // interno de la librería que recomputa el priceScale fresco. Aquí, ya
        // con el logical range final aplicado (restaurado o default), para que
        // recompute sobre la ventana correcta — un solo toque por cambio de
        // símbolo/TF (dentro del guard !initialRestoreDoneRef de más arriba),
        // no en cada poll/incremental.
        try {
          liveChart.priceScale('right').applyOptions({ autoScale: true })
        } catch {}
      }, 0)
      return () => clearTimeout(restoreTimeoutId)
    }
  }, [data, dataRef, dataVersion, chartType, seriesEpoch]) // eslint-disable-line react-hooks/exhaustive-deps

  // Canal en vivo: aplica la vela en formación (prop `liveCandle`) con
  // series.update() incremental. NO toca prevDataRef — cuando el poll REST
  // trae esa misma vela (ya con datos de cierre), el efecto de datos la
  // reconcilia por su camino normal (mismo time → update incremental).
  //
  // update() solo es legal sobre la última vela real de la serie, y durante
  // una transición de símbolo/TF NO hay última vela válida: los guards de
  // abajo (en orden) son la garantía; el try/catch es solo la última línea.
  // Los descartes del canal en vivo se loguean UNA vez por motivo por el
  // logger gateado (los ticks llegan cada segundo — sin el dedup, incluso en
  // modo debug la consola se inunda y el log deja de servir).
  const logLiveDiscard = (motivo, detalle) => {
    const key = `${motivo}:${detalle?.vigente ?? ''}:${detalle?.tickSymbol ?? detalle?.tickTimeframe ?? ''}`
    if (liveDiscardLoggedRef.current.has(key)) return
    liveDiscardLoggedRef.current.add(key)
    debugLog(`[CHART-GUARD] renderer: tick en vivo DESCARTADO (${motivo}):`, detalle)
  }
  useEffect(() => {
    const series = seriesRef.current
    if (!series || !liveCandle || liveCandle.time == null) return
    // 1) Transición en curso: hasta que el efecto de datos complete el
    //    setData del histórico nuevo, todo tick se descarta sin tocar la
    //    serie (un update aquí abortaba la transición con "Cannot update
    //    oldest data" y dejaba el chart en blanco con el eje stale).
    if (transitioningRef.current) {
      logLiveDiscard('transicion-en-curso', { symbol, timeframe, tickSymbol: liveCandle.symbol ?? null })
      return
    }
    // 2) Anti-mezcla: la vela viene etiquetada por useLiveCandle con el
    //    símbolo/TF de su suscripción (tanto WS como fallback REST); un tick
    //    del símbolo/TF viejo en vuelo durante el cambio se descarta.
    if (liveCandle.symbol != null && liveCandle.symbol !== symbol) {
      logLiveDiscard('symbol-mismatch', { tickSymbol: liveCandle.symbol, vigente: symbol })
      return
    }
    if (liveCandle.timeframe != null && liveCandle.timeframe !== timeframe) {
      logLiveDiscard('timeframe-mismatch', { tickTimeframe: liveCandle.timeframe, vigente: timeframe })
      return
    }
    const isCandles = chartTypeRef.current === 'candles'
    const valuesOk = isCandles
      ? [liveCandle.open, liveCandle.high, liveCandle.low, liveCandle.close].every(Number.isFinite)
      : Number.isFinite(liveCandle.close)
    if (!valuesOk) return
    // 3) Stale: ticks con time anterior a la última vela del histórico
    //    aplicado, al último tick en vivo aplicado o a la última vela REAL de
    //    la serie (puede ir por delante del histórico si un tick en vivo ya
    //    abrió la vela siguiente) se descartan sin tocar la serie.
    const prev = prevDataRef.current
    const lastHistTime = prev && prev.length > 0 ? prev[prev.length - 1].time : null
    if (lastHistTime != null && liveCandle.time < lastHistTime) {
      logLiveDiscard('stale-vs-historico', { vigente: symbol, tickTime: liveCandle.time, lastHistTime })
      return
    }
    if (lastLiveTimeRef.current != null && liveCandle.time < lastLiveTimeRef.current) return
    const seriesBars = series.data()
    const lastSeriesTime = seriesBars.length > 0 ? seriesBars[seriesBars.length - 1].time : null
    if (lastSeriesTime != null && liveCandle.time < lastSeriesTime) {
      logLiveDiscard('stale-vs-serie', { vigente: symbol, tickTime: liveCandle.time, lastSeriesTime })
      return
    }
    // Las etiquetas symbol/timeframe no forman parte de la vela.
    const { symbol: _tickSymbol, timeframe: _tickTf, ...bar } = liveCandle
    try {
      if (isCandles) {
        series.update(applyCandleColor(bar, getCandleColorMap(candleColorsRef)))
      } else {
        series.update({ time: bar.time, value: bar.close })
      }
      lastLiveTimeRef.current = bar.time
    } catch (e) {
      // Última línea de defensa, nunca el mecanismo: la garantía es el orden
      // de arriba. Si aun así lwc rechaza el update, queda logueado por el
      // logger gateado (visible con el modo debug) y el poll REST reconcilia
      // la serie.
      debugWarn('[ChartRenderer] update en vivo rechazado:', e?.message || e)
    }
  }, [liveCandle, chartType, candleColorsRef, symbol, timeframe])

  // Aplica/quita colores por-vela reconstruyendo la serie con setData().
  // NO usar series.update() aquí: lightweight-charts solo permite update()
  // sobre la última vela — para cualquier vela histórica lanza "Cannot
  // update oldest data", que el try/catch por-vela se tragaba en silencio.
  // Ese es el bug raíz del "Colorear velas" de supertrend_quantum: el
  // coloreado y el restore solo se aplicaban de verdad cuando el efecto de
  // datos hacía su setData completo, así que quitar el indicador dejaba las
  // velas coloreadas y recargar con el indicador activo no coloreaba.
  // setData() reescribe toda la serie de una vez; el rango visible se
  // preserva explícitamente para que la vista no salte al borde derecho.
  //
  // Reglas de ciclo de vida:
  // - NO se consume colorVersion si no se pudo aplicar (sin serie o sin
  //   datos todavía): la versión queda pendiente y se reintenta en el
  //   siguiente disparo del efecto (p.ej. cuando llegan los datos, de ahí
  //   `dataVersion` en las dependencias). Antes se consumía al entrar y la
  //   aplicación se perdía silenciosamente.
  // - Restore = setData con las velas tal cual, SIN color por vela: pasan a
  //   heredar las opciones de serie, es decir la paleta activa en ese
  //   momento — nunca una paleta horneada vieja.
  // - Un cambio de paleta también dispara la pasada: si la serie tenía
  //   colores horneados por vela de un coloreado anterior (los colores por
  //   vela ganan a las opciones de serie), hay que reescribirla para que
  //   mande la paleta nueva.
  const prevColorVersionRef = useRef(colorVersion)
  const prevColorPaletteRef = useRef(palette)
  useEffect(() => {
    const versionChanged = colorVersion !== prevColorVersionRef.current
    const paletteChanged = palette !== prevColorPaletteRef.current
    if (!versionChanged && !paletteChanged) return
    const series = seriesRef.current
    const chart = chartRef.current
    if (!series || !chart || chartType !== 'candles') return
    const currentData = dataRef?.current ?? data ?? []
    if (!currentData || currentData.length === 0) return
    const sanitized = sanitizeCandles(currentData)
    if (sanitized.length === 0) return
    // Solo a partir de aquí, con aplicación garantizada, se consumen los
    // cambios pendientes.
    prevColorVersionRef.current = colorVersion
    prevColorPaletteRef.current = palette
    const colorMap = getCandleColorMap(candleColorsRef)
    let visibleRange = null
    try { visibleRange = chart.timeScale().getVisibleLogicalRange() } catch {}
    const applied = applyMainSetData(sanitized.map(c => applyCandleColor(c, colorMap)), 'color-effect')
    if (!applied) return
    // Mantener coherente la detección incremental del efecto de datos: la
    // serie acaba de quedar exactamente con estos datos.
    prevDataRef.current = sanitized
    if (visibleRange) {
      try {
        // El re-apply es programático (no un gesto del usuario): no debe
        // persistirse como "zoom del usuario" en localStorage.
        ignoreRangeChangeRef.current = true
        chart.timeScale().setVisibleLogicalRange(visibleRange)
      } catch {}
    }
    debugLog('[ChartRenderer color effect] applied', { colorVersion, candles: sanitized.length, colored: !!colorMap, paletteChanged })
  }, [colorVersion, chartType, data, dataRef, dataVersion, candleColorsRef, palette])

  // Persist user zoom changes to localStorage (per symbol)
  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    const handler = (range) => {
      if (restorePendingRef.current) return
      if (ignoreRangeChangeRef.current) {
        ignoreRangeChangeRef.current = false
        return
      }
      if (!range) return
      const saved = encodeSavedRange(range, lastBarLogical(chart, seriesRef.current))
      if (!saved) return
      try { localStorage.setItem(rangeKeyRef.current, JSON.stringify(saved)) } catch {}
    }
    chart.timeScale().subscribeVisibleLogicalRangeChange(handler)
    return () => chart.timeScale().unsubscribeVisibleLogicalRangeChange(handler)
  }, [])

  // Subscribe to real-time candle updates without re-rendering parent.
  // In playback mode the parent drives full setData via dataVersion, so we
  // skip the incremental update to avoid “cannot update oldest data” errors.
  //
  // Se usa setData() (reconstrucción completa) en vez de update() (incremental)
  // a propósito: update() asume que la caché interna de lightweight-charts para
  // la serie ya está consistente, pero justo tras un cambio de timeframe el
  // efecto de indicadores (ChartPage) puede haber añadido/quitado decenas de
  // series auxiliares momentos antes — esa combinación (churn de series +
  // update() incremental) es la que reproducíamos como "Value is null"
  // diferido. setData() siempre reconstruye desde cero y no depende de ese
  // estado intermedio.
  //
  // ADEMÁS: los ticks entrantes no aplican setData síncronamente. Se encolan
  // en pendingDataRef y se aplican en un único RAF. Esto asegura que el setData
  // de la serie principal nunca interfiere con el render síncrono de los
  // indicadores overlay, cerrando la carrera que crasheaba al activar STQ con
  // datos en vivo.
  useEffect(() => {
    if (!subscribe || !dataRef || disableRealtimeUpdates) return
    const pendingDataRef = { current: null }
    let flushId = null
    const flush = () => {
      flushId = null
      const series = seriesRef.current
      if (!series) return
      const sanitized = pendingDataRef.current
      pendingDataRef.current = null
      if (!sanitized || sanitized.length === 0) return
      series.applyOptions({ priceFormat: computePriceFormat(sanitized) })
      if (chartTypeRef.current === 'candles') {
        if (sanitized.some(c => c.time == null || c.time === '' || Number.isNaN(c.time) || ![c.open, c.high, c.low, c.close].every(Number.isFinite))) return
        applyMainSetData(sanitized.map(c => applyCandleColor(c, getCandleColorMap(candleColorsRef))), 'realtime-flush')
      } else {
        if (sanitized.some(c => c.time == null || c.time === '' || Number.isNaN(c.time) || !Number.isFinite(c.close))) return
        applyMainSetData(sanitized.map(c => ({ time: c.time, value: c.close })), 'realtime-flush')
      }
    }
    return subscribe(() => {
      window.__lastTickAt = Date.now()
      const series = seriesRef.current
      if (!series) return
      // Tras un cambio de símbolo, no repintar ticks con datos del símbolo
      // anterior: la serie quedó vacía hasta que lleguen las velas nuevas
      // (mismo guard que el efecto de datos).
      if (awaitingNewSymbolDataRef.current && dataRef.current === dataAtResetRef.current) return
      const live = dataRef.current
      if (!live || live.length === 0) return
      const sanitized = sanitizeCandles(live)
      if (sanitized.length === 0) return
      pendingDataRef.current = sanitized
      if (flushId) return
      // Aplazar un frame para que React termine cualquier render de
      // indicadores que esté en curso antes de tocar la serie principal.
      flushId = requestAnimationFrame(flush)
    })
  }, [subscribe, dataRef, disableRealtimeUpdates])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    chart.applyOptions({
      layout: {
        background: { color: bg.bg || (isDark ? '#111827' : '#ffffff') },
        textColor:  bg.text || (isDark ? '#94a3b8' : '#64748b'),
      },
      grid: {
        vertLines: { color: bg.grid || (isDark ? '#1f2937' : '#f1f5f9') },
        horzLines: { color: bg.grid || (isDark ? '#1f2937' : '#f1f5f9') },
      },
    })
    if (chartType === 'candles') {
      seriesRef.current?.applyOptions({
        upColor: pal.up, downColor: pal.down,
        wickUpColor: pal.up, wickDownColor: pal.down,
      })
    } else {
      seriesRef.current?.applyOptions({ color: pal.up, lineColor: pal.up })
    }
  }, [palette, chartBg, bgOverride, isDark, chartType])

  // Aplica el cambio de zona horaria en caliente, sin recargar ni reconstruir
  // el chart. Actualiza eje, crosshair y tooltips al instante.
  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return
    const { timeFormatter, dateFormatter, tickMarkFormatter } = createTimeFormatters(timeDisplay)
    chart.applyOptions({
      timeScale: { tickMarkFormatter },
      localization: { timeFormatter, dateFormatter },
    })
  }, [timeDisplay])

  return (
    <div
      ref={containerRef}
      className="w-full h-full"
      style={{ position: 'relative', cursor: tradingMode ? 'crosshair' : undefined, touchAction: 'none' }}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
    />
  )
}
